import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import { AuthContext } from '../reducers/Auth/AuthContext';
import Button from '@mui/material/Button';
import { EditField } from './EditField';
import { EditYesNo } from './EditYesNo';
import { EditExtraToppingGroups } from './EditExtraToppingGroups';
import { EditToppingGroup } from './EditToppingGroup';
import BarLoader from 'react-spinners/BarLoader';
import { uuid } from '../utils/uuid';
import { PlaceStoreContext } from '../reducers/Place/PlaceContext';
import { updateTopping } from '../apis/updateTopping';
import { deleteTopping } from '../apis/deleteTopping';
import { addNewTopping } from '../apis/addNewTopping';
import AreYouSureYesNo from '../components/AreYouSureYesNo';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { COLORS } from '../utils/colors';
import PublishButton from './PublishButton';

const ManageTopping = (props) => {
  const { toppingId, onClose, type, toppingGroupId } = props;
  const [placeState] = useContext(PlaceStoreContext);
  const [authState] = useContext(AuthContext);
  const [initialData, setInitialData] = useState(null);
  const {
    toppings: { toppingId__topping, toppingGroupId__toppingsGroups },
  } = placeState;
  const {
    user: { placeId },
  } = authState;
  const history = useHistory();

  const [toppingDelete, setToppingDelete] = useState({
    visible: false,
    isDeleting: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let initialValues = null;
    if (type === 'new') {
      initialValues = {
        id: uuid(),
        name: '',
        toppings_group_id: toppingGroupId,
        cost: '',
        code: '',
        active: true,
        extra_grp: [],
      };
    } else if (toppingId) {
      const topping = toppingId__topping[toppingId];
      if (!topping) {
        onClose();
        return;
      }
      initialValues = {
        id: topping.id,
        name: topping.name,
        toppings_group_id: topping.toppings_group_id,
        cost: topping.cost,
        weight: topping.weight || '',
        code: topping.code,
        active: topping.active,
        extra_grp: [],
      };
    }
    initialValues && setInitialData({ initialValues });
    if (toppingDelete.visible) {
      setToppingDelete({ visible: false, isDeleting: false });
      // history.replace(`/manage/toppings`);
      onClose();
    }
  }, [placeState, toppingId, type]);

  const handleToppingDelete = () => {
    setToppingDelete({ visible: true, isDeleting: false });
  };

  const toppingOnNo = () => {
    setToppingDelete({ visible: false, isDeleting: false });
  };

  const toppingOnYes = () => {
    setToppingDelete({ visible: true, isDeleting: true });
    deleteTopping(placeId, toppingId);
  };

  if (initialData === null) return null;

  if (toppingDelete.isDeleting) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: `calc(90vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
          height: '80vh',
          margin: '0 auto',
        }}
      >
        <Grid container spacing={2} style={{ padding: '16px' }}>
          <Grid item xs={12}>
            <AreYouSureYesNo
              onNo={toppingOnNo}
              onYes={toppingOnYes}
              loading={toppingDelete.isDeleting}
              loadingMessage="Deleting topping ..."
              warning="This topping will be deleted!"
            />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Grid container style={{ marginTop: '20px' }}>
      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={initialData.initialValues}
          validationSchema={Yup.object({
            name: Yup.string()
              .min(3, 'Must have at least 3 chars')
              .max(64, 'Must be 32 characters or less')
              .required('Required'),
            code: Yup.string()
              .max(36, 'Must be 36 characters or less'),
            cost: Yup.string()
              .matches(/^(\d+(\.\d{0,2})?|\.?\d{1,2})$/, 'Not a valid cost.')
              .test('value', 'Must be between 0 and 10000000', (value) => {
                return Number(value) >= 0 && Number(value) < 10000000;
              })
              .test('value', 'Must have at least one character', (value) => value && value.trim().length >= 1)
              .required('Required'),
            weight: Yup.string()
              .max(64, 'Must be 64 characters or less'),
            active: Yup.boolean().required('Required'),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            console.log('SUBMITTING...');
            if (!placeId) return;
            setSubmitting(true);

            if (type === 'edit') {
              await updateTopping(placeId, toppingId, values);
            } else if (type === 'new') {
              await addNewTopping(placeId, values);
            }

            setSubmitting(false);
            onClose();
            console.log('DONE SUBMITTING...');
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => {
            if (isSubmitting)
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: `calc(100vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
                    height: '80vh',
                    margin: '0 auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <p style={{ margin: 0, paddingBottom: '20px', fontWeight: 'bold' }}>
                      SAVING CHANGES
                    </p>
                    <BarLoader height={4} width={100} color="#000000" loading={true} />
                  </div>
                </div>
              );
            const submitDisabled = isSubmitting || _.isEqual(initialData.initialValues, values);
            const title =
              toppingId === 'new' && toppingGroupId
                ? `NEW TOPPING FOR ${toppingGroupId__toppingsGroups[
                    toppingGroupId
                  ].name.toUpperCase()}`
                : toppingId__topping[toppingId].name.toUpperCase();
            return (
              <div>
                <Form encType="multipart/form-data">
                  <Grid container spacing={2} justify="center" style={{ maxWidth: '500px' }}>
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                          fontWeight: 'bold',
                          color: COLORS.INFO,
                        }}
                      >
                        {title}
                      </p>
                      <PublishButton name="PUBLISH" disabled={submitDisabled} />
                    </Grid>
                    <Grid item xs={12}>
                      <EditYesNo label="Active" name="active" />
                    </Grid>
                    <Grid item xs={12}>
                      <EditField label="Name (64 max characters)" name="name" />
                    </Grid>
                    <Grid item xs={12}>
                      <EditField label="Code (36 max characters)" name="code" />
                    </Grid>
                    <Grid item xs={12}>
                      <EditField label={`Cost`} name="cost" />
                    </Grid>
                    <Grid item xs={12}>
                      <EditToppingGroup
                        label="Topping Group"
                        name="toppings_group_id"
                        selected={initialData.initialValues.toppings_group_id}
                      />
                    </Grid>
                    <Grid item xs={12}>
                    <EditField
                        label={`Units`}
                        name="weight"
                        style={{ paddingBottom: '15px' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <EditExtraToppingGroups
                        disabled
                        mainToppingGroup={values.toppings_group_id}
                        extraToppingGroups={values.extra_grp}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                    {type !== 'new' && (
                      <Grid item xs={12} style={{ marginTop: 16 }}>
                        {!toppingDelete.visible ? (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={handleToppingDelete}
                          >
                            <span>DELETE</span>
                            <DeleteForeverIcon className="s-24-i txt-white ml-16" />
                          </Button>
                        ) : (
                          <div className="ml-16">
                            <AreYouSureYesNo
                              onNo={toppingOnNo}
                              onYes={toppingOnYes}
                              loading={toppingDelete.isDeleting}
                              loadingMessage="Deleting topping ..."
                              warning="This topping will be deleted!"
                            />
                          </div>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Form>
              </div>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default ManageTopping;
